import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Badge,
  Typography,
  Checkbox,
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useInvoices } from '@hooks/useFinance';
import { useCustomerSearch } from '@hooks/useSales';
import type {
  InvoiceListItem,
  InvoiceStatus,
  InvoiceFilters,
} from '@/types/finance';
import { downloadArExcel } from '@/api/exportApi';
import { useAuth } from '@stores/AuthContext';
import { mdCanReadModule, mdCanWriteModule } from '@stores/modulePermissions';
import CreateInvoiceModal from './CreateInvoiceModal';

const { RangePicker } = DatePicker;

const MD_STATUS_OPTIONS: { value: InvoiceStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Draft', label: 'Чернетка' },
  { value: 'Issued', label: 'Виставлено' },
  { value: 'PartiallyPaid', label: 'Частково сплачено' },
  { value: 'Paid', label: 'Сплачено' },
  { value: 'Overdue', label: 'Прострочено' },
  { value: 'Cancelled', label: 'Скасовано' },
];

const MD_STATUS_COLOR_MAP: Record<InvoiceStatus, string> = {
  Draft: 'default',
  Issued: 'processing',
  PartiallyPaid: 'warning',
  Paid: 'success',
  Overdue: 'error',
  Cancelled: 'default',
};

const MD_STATUS_LABEL_MAP: Record<InvoiceStatus, string> = {
  Draft: 'Чернетка',
  Issued: 'Виставлено',
  PartiallyPaid: 'Частково сплачено',
  Paid: 'Сплачено',
  Overdue: 'Прострочено',
  Cancelled: 'Скасовано',
};

interface InvoicesPageProps {
  /** Рахунок уже створено — відкрити його картку */
  onInvoiceCreated: (mdInvoiceId: string) => void;
  onViewInvoice: (mdInvoiceId: string) => void;
}

export default function InvoicesPage({
  onInvoiceCreated,
  onViewInvoice,
}: InvoicesPageProps) {
  const { user } = useAuth();
  const mdRoles = user?.roles ?? [];
  // POST /api/invoices потребує повного доступу до Finance, а вибір клієнта — читання Customers
  const mdCanCreateInvoice = mdCanWriteModule(mdRoles, 'Finance') && mdCanReadModule(mdRoles, 'Customers');
  const [mdIsCreateModalOpen, setMdIsCreateModalOpen] = useState(false);
  const [mdSearch, setMdSearch] = useState('');
  const [mdCustomerFilter, setMdCustomerFilter] = useState<string | undefined>();
  const [mdStatusFilter, setMdStatusFilter] = useState<InvoiceStatus | ''>('');
  const [mdOverdueOnly, setMdOverdueOnly] = useState(false);
  const [mdDateRange, setMdDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    null
  );
  const [mdCustomerSearchText, setMdCustomerSearchText] = useState('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const { data: mdCustomerOptions } = useCustomerSearch(mdCustomerSearchText);

  const mdFilters: InvoiceFilters = useMemo(() => ({
    search: mdSearch || undefined,
    customerId: mdCustomerFilter,
    status: mdStatusFilter || undefined,
    overdue: mdOverdueOnly ? true : undefined,
    dateFrom: mdDateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: mdDateRange?.[1]?.format('YYYY-MM-DD'),
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdCustomerFilter, mdStatusFilter, mdOverdueOnly, mdDateRange, mdPage, mdPageSize]);

  const [mdExportLoading, setMdExportLoading] = useState(false);
  const { data: mdInvoicesData, isLoading: mdIsLoading } = useInvoices(mdFilters);

  const mdHandleExportExcel = async () => {
    setMdExportLoading(true);
    try {
      await downloadArExcel();
    } finally {
      setMdExportLoading(false);
    }
  };

  const mdColumns: ColumnsType<InvoiceListItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      sorter: (mdA, mdB) => mdA.number.localeCompare(mdB.number),
    },
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: 'Дата',
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
      sorter: (mdA, mdB) => mdA.issueDate.localeCompare(mdB.issueDate),
    },
    {
      title: 'Термін оплати',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 130,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
    },
    {
      title: 'Сума',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      align: 'right',
      render: (mdValue: number, mdRecord: InvoiceListItem) =>
        `${mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} ${mdRecord.currency}`,
    },
    {
      title: 'Сплачено',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 140,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Залишок',
      dataIndex: 'balanceDue',
      key: 'balanceDue',
      width: 140,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (mdValue: InvoiceStatus) => (
        <Badge
          status={MD_STATUS_COLOR_MAP[mdValue] as 'default' | 'processing' | 'success' | 'error' | 'warning'}
          text={MD_STATUS_LABEL_MAP[mdValue]}
        />
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Рахунки
        </Typography.Title>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={mdHandleExportExcel}
            loading={mdExportLoading}
          >
            Завантажити Excel
          </Button>
          {mdCanCreateInvoice && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setMdIsCreateModalOpen(true)}>
              Створити рахунок
            </Button>
          )}
        </Space>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за номером..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Клієнт"
          filterOption={false}
          onSearch={setMdCustomerSearchText}
          onChange={(mdValue: string | undefined) => {
            setMdCustomerFilter(mdValue);
            setMdPage(1);
          }}
          options={mdCustomerOptions?.map((mdCust) => ({
            value: mdCust.id,
            label: `${mdCust.code} — ${mdCust.name}`,
          }))}
          style={{ width: 250 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 200 }}
        />
        <Checkbox
          checked={mdOverdueOnly}
          onChange={(mdEvent) => {
            setMdOverdueOnly(mdEvent.target.checked);
            setMdPage(1);
          }}
        >
          Тільки прострочені
        </Checkbox>
        <RangePicker
          format="DD.MM.YYYY"
          onChange={(mdDates) => {
            setMdDateRange(mdDates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null);
            setMdPage(1);
          }}
          placeholder={['Дата від', 'Дата до']}
        />
      </Space>

      <Table<InvoiceListItem>
        columns={mdColumns}
        dataSource={mdInvoicesData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewInvoice(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        rowClassName={(mdRecord) => (mdRecord.isOverdue ? 'ant-table-row-overdue' : '')}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdInvoicesData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <CreateInvoiceModal
        open={mdIsCreateModalOpen}
        onClose={() => setMdIsCreateModalOpen(false)}
        onCreated={(mdInvoiceId) => {
          setMdIsCreateModalOpen(false);
          onInvoiceCreated(mdInvoiceId);
        }}
      />

      <style>{`
        .ant-table-row-overdue td {
          background-color: #fff2f0 !important;
        }
        .ant-table-row-overdue:hover td {
          background-color: #ffebe8 !important;
        }
      `}</style>
    </>
  );
}
